export const filterLots = (lots = [], searchTerm = '') => {
  if (!searchTerm) return lots;
  const term = searchTerm.toLowerCase();
  return lots.filter((lot) => {
    const lotNumber = String(lot?.lot_number || '').toLowerCase();
    const blockNumber = String(lot?.block_number || '').toLowerCase();
    const cadastral = String(lot?.cadastral_number || '').toLowerCase();
    return lotNumber.includes(term) || blockNumber.includes(term) || cadastral.includes(term);
  });
};

export const clampPercentage = (value) => Math.max(0, Math.min(100, value));

export const getLotStatusLabel = (status) => {
  if (!status) return '-';
  const normalized = String(status).replace('LotStatusEnum.', '').toLowerCase();
  const labelMap = {
    no_disponible: 'No disponible',
    disponible: 'Disponible',
    vinculado: 'Vinculado',
  };
  return labelMap[normalized] || status;
};

export const normalizeLotStatusValue = (status) => {
  if (!status) return '';
  return String(status).replace('LotStatusEnum.', '').toLowerCase();
};

const VON_DER_HEYDE_NAMES = [
  'marcelo von der heyde',
  'guillermo von der heyde',
  'maria lucrecia von der heyde',
  'florencia von der heyde',
];

const normalizeName = (value) => String(value || '').trim().toLowerCase();

export const getLotReferenceStyle = ({ lot, project, progressSummary, qualitySummary }) => {
  const status = normalizeLotStatusValue(lot?.status);
  const personName = normalizeName(lot?.person_name);
  const isVonDerHeyde = VON_DER_HEYDE_NAMES.includes(personName);
  const isColonial = personName === 'constructora colonial';
  const hasLotTicket =
    Array.isArray(lot?.lot_tickets) &&
    lot.lot_tickets.length > 0 &&
    lot.lot_tickets.some((ticket) => ticket && ticket.uuid);
  const hasContract = Boolean(lot?.uuid_contract);

  if (status === 'no_disponible') {
    return {
      backgroundColor: '#9e9e9e',
      color: '#ffffff',
      borderColor: '#858585',
      spinner: false,
    };
  }

  if (status === 'disponible') {
    return {
      backgroundColor: '#2e7d32',
      color: '#ffffff',
      borderColor: '#246727',
      spinner: false,
    };
  }

  if (status !== 'vinculado') {
    return {
      backgroundColor: '#90a4ae',
      color: '#ffffff',
      borderColor: '#546e7a',
      spinner: false,
    };
  }

  const hasProject = Boolean(project?.uuid);
  const projectStatusInProgress = project?.status === 'in_progress';
  const projectStatusCompleted = project?.status === 'completed';
  const isFullyCompleted = projectStatusCompleted;
  const isInProgress = projectStatusInProgress;

  if (isColonial && hasProject && !isInProgress && !isFullyCompleted) {
    return {
      backgroundColor: '#ff9800',
      color: '#111111',
      borderColor: '#da8300',
      spinner: true,
      spinnerColor: '#ffb74d',
    };
  }

  if (status === 'vinculado' && hasContract && !isInProgress && !isFullyCompleted) {
    return {
      backgroundColor: '#1565c0',
      color: '#ffffff',
      borderColor: '#135bae',
      spinner: false,
    };
  }

  if (isFullyCompleted) {
    return {
      backgroundColor: '#d32f2f',
      color: '#ffffff',
      borderColor: '#b92a2a',
      spinner: false,
    };
  }

  if (!hasProject || (!isInProgress && !isFullyCompleted)) {
    if (status === 'vinculado' && hasLotTicket) {
      return {
        backgroundColor: '#81d4fa',
        color: '#111111',
        borderColor: '#4fc3f7',
        spinner: false,
      };
    }
    if (isVonDerHeyde) {
      return {
        backgroundColor: '#1b1b1b',
        color: '#ffffff',
        borderColor: '#4d4d4d',
        spinner: false,
      };
    }
    if (isColonial) {
      return {
        backgroundColor: '#ff9800',
        color: '#111111',
        borderColor: '#da8300',
        spinner: hasProject,
        spinnerColor: hasProject ? '#ffb74d' : undefined,
      };
    }
    return {
      backgroundColor: '#ffffff',
      color: '#111111',
      borderColor: '#d0d0d0',
      spinner: false,
    };
  }

  if (isInProgress) {
    if (isVonDerHeyde) {
      return {
        backgroundColor: '#1b1b1b',
        color: '#ffffff',
        borderColor: '#4d4d4d',
        spinner: true,
      };
    }
    if (isColonial) {
      return {
        backgroundColor: '#ff9800',
        color: '#111111',
        borderColor: '#da8300',
        spinner: true,
        spinnerColor: '#ffb74d',
      };
    }
    return {
      backgroundColor: '#1565c0',
      color: '#ffffff',
      borderColor: '#135bae',
      spinner: true,
    };
  }

  return {
    backgroundColor: '#ffffff',
    color: '#111111',
    borderColor: '#d0d0d0',
    spinner: false,
  };
};

export const getLotReferenceLabel = ({ lot, project }) => {
  const status = normalizeLotStatusValue(lot?.status);
  const personName = normalizeName(lot?.person_name);
  const isVonDerHeyde = VON_DER_HEYDE_NAMES.includes(personName);
  const isColonial = personName === 'constructora colonial';
  const hasLotTicket =
    Array.isArray(lot?.lot_tickets) &&
    lot.lot_tickets.length > 0 &&
    lot.lot_tickets.some((ticket) => ticket && ticket.uuid);
  const hasContract = Boolean(lot?.uuid_contract);

  if (status === 'no_disponible') {
    return 'No disponible';
  }

  if (status === 'disponible') {
    return 'Disponible';
  }

  if (status !== 'vinculado') {
    return 'Asignado a un contrato';
  }

  const projectStatusInProgress = project?.status === 'in_progress';
  const projectStatusCompleted = project?.status === 'completed';
  const hasProject = Boolean(project?.uuid);
  const isFullyCompleted = projectStatusCompleted;
  const isInProgress = projectStatusInProgress;

  if (status === 'vinculado' && hasContract && !isInProgress && !isFullyCompleted) {
    return 'Asignado a un contrato';
  }

  if (isFullyCompleted) {
    return 'Obra completada';
  }

  if (!hasProject || (!isInProgress && !isFullyCompleted)) {
    if (status === 'vinculado' && hasLotTicket) {
      return 'Boleto asociado';
    }
    if (isVonDerHeyde) {
      return 'Asignado a Von Der Heyde';
    }
    if (isColonial) {
      return 'Asignado a Constructora Colonial';
    }
    return 'Asignado a un contrato';
  }

  if (isInProgress) {
    return 'Obra en proceso';
  }

  return 'Asignado a un contrato';
};
